import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { Allergy } from "@/lib/types";
import { deleteAllergy, getAllergy } from "@/sideEffects/api";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { showSuccess, showApiError } from "@/lib/toast";
import { useQueryClient, useMutation } from "@tanstack/react-query";

export const AllergyPage = () => {
  const { t } = useTranslation();

  const { goToAllergyAdd, goToAllergyDetail } = useAppNavigate();

  const columns: ColumnDef<Allergy>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
    },
    {
      accessorKey: "id",
      header: () => "",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end gap-2">
            <UpdateRowButton onClickUpdate={() => goToAllergyDetail(row.original.id)} />
          </div>
        );
      },
    },
  ];

  useBreadcrumbs([
    {
      name: t("entities.allergy"),
      path: ROUTES.ALLERGY,
    },
  ]);

  const queryClient = useQueryClient();
  const deleteAllergiesMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deleteAllergy(Number(id))));
    },
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getAllergy"] });
    },
    onError: showApiError,
  });

  return (
    <div className="flex gap-4">
      <AxDataTable
        columns={columns}
        fetcher={getAllergy}
        getRowId={(row) => row.id.toString()}
        uniqQueryKey="getAllergy"
        extraHeader={
          <AddRowButton
            onClickAdd={() => goToAllergyAdd()}
            title={t("common.addNewEntity", { entity: t("entities.allergy") })}
          />
        }
        onDeleteRows={deleteAllergiesMutation.mutateAsync}
        deleting={deleteAllergiesMutation.isPending}
      />
    </div>
  );
};
